import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Blue2Page, Part } from "../Page";
import { EASE, PAGES } from "../bits";
import { AddButton, CartButton, PriceTag, ProductCard, ProductPicture, ShopCart, StockNote, useCartUI, useProducts } from "./Shop";
import "./Shop.css";
import "./Product.css";

/* blue-2 product page: the same /api/products/:slug data and cart as the
   current product page, restyled, with a few more goods underneath. */

type Product = {
  id: string; slug: string; name: string; brand: string; description: string;
  price: number; inventory: number; featured?: boolean;
  images: { mediaId: string; url: string | null; alt: string | null }[];
  imageUrl: string | null;
};

type State = { status: "loading" } | { status: "missing" } | { status: "error" } | { status: "ok"; product: Product };

function Header({ name }: { name?: string }) {
  return <div className="bp-head">
    <nav className="bp-crumbs" aria-label="Breadcrumb">
      <Link to={PAGES.shop}><ArrowLeft size={16} aria-hidden="true" /> Shop</Link>
      {name && <><span aria-hidden="true">/</span><span aria-current="page">{name}</span></>}
    </nav>
    <CartButton />
  </div>;
}

function Gallery({ product }: { product: Product }) {
  const pics = product.images.filter(i => i.url);
  const [on, setOn] = useState(0);
  const current = pics[on];
  return <div className="bp-gallery">
    <motion.figure className="bp-polaroid" initial={{ opacity: 0, y: 30, rotate: 4 }} animate={{ opacity: 1, y: 0, rotate: -2 }} transition={{ type: "spring", stiffness: 120, damping: 15 }}>
      <i className="bs-tape bs-tape-pop" aria-hidden="true" />
      <ProductPicture src={current?.url ?? product.imageUrl} name={product.name} brand={product.brand} alt={current?.alt ?? product.name} />
      {product.featured && <span className="bs-pick" aria-hidden="true">Chelsea’s<br />pick!</span>}
    </motion.figure>
    {pics.length > 1 && <div className="bp-thumbs" role="group" aria-label="Product photos">
      {pics.map((p, i) => <button key={p.mediaId} type="button" aria-pressed={i === on} aria-label={`Photo ${i + 1} of ${pics.length}`} onClick={() => setOn(i)}>
        <img src={p.url!} alt="" />
      </button>)}
    </div>}
  </div>;
}

function Details({ product }: { product: Product }) {
  const { open } = useCartUI();
  const hero = product.images.find(i => i.url)?.url ?? product.imageUrl;
  const item = { productId: product.id, slug: product.slug, name: product.name, brand: product.brand, price: product.price, imageUrl: hero };
  return <motion.div className="bp-info" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5, ease: EASE }}>
    <span className="bs-brand">{product.brand}</span>
    <h1 className="bp-name">{product.name}</h1>
    <PriceTag cents={product.price} big />
    {product.description && <p className="bp-desc">{product.description}</p>}
    <StockNote inventory={product.inventory} />
    <div className="bp-actions">
      <AddButton item={item} outOfStock={product.inventory <= 0} big />
      <button type="button" className="bp-view" onClick={open}>View cart</button>
    </div>
    <Link to={PAGES.shop} className="bs-more"><ArrowLeft size={15} aria-hidden="true" /> Back to the shop</Link>
  </motion.div>;
}

function More({ slug }: { slug: string }) {
  const { data } = useProducts();
  const others = (data?.products ?? []).filter(p => p.slug !== slug).slice(0, 3);
  if (!others.length) return null;
  return <section className="bp-more" aria-labelledby="bp-more-title">
    <h2 id="bp-more-title" className="b2-note bp-more-hand">a few more goods</h2>
    <ul className="bs-grid">{others.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}</ul>
  </section>;
}

export function Blue2Product() {
  const { slug = "" } = useParams<{ slug: string }>();
  const [state, setState] = useState<State>({ status: "loading" });
  useEffect(() => {
    setState({ status: "loading" });
    api.get<{ product: Product }>(`/api/products/${slug}`)
      .then(r => setState({ status: "ok", product: r.product }))
      .catch((e: { status?: number }) => setState({ status: e.status === 404 ? "missing" : "error" }));
  }, [slug]);

  const name = state.status === "ok" ? state.product.name : undefined;
  return <ShopCart>
    <Blue2Page current="shop" tone="blush" header={<Header name={name} />}>
      <Part p="faves">
        <section className="b2-sec bp">
          <div className="b2-wrap">
            {state.status === "loading" && <div className="bp-grid" aria-label="Loading product"><div className="bp-skel bp-skel-pic" /><div className="bp-skel bp-skel-info" /></div>}
            {state.status === "missing" && <div className="bp-missing">
              <p className="b2-note">hmm, that one’s not here</p>
              <h1 className="bp-name">We couldn’t find that product.</h1>
              <Link to={PAGES.shop} className="b2-btn b2-btn-blue">Back to the shop <ArrowRight size={18} aria-hidden="true" /></Link>
            </div>}
            {state.status === "error" && <p className="bs-state" role="alert">This product didn’t load. Try again in a minute? <span className="bs-state-hand">(it’s not you, it’s the internet)</span></p>}
            {state.status === "ok" && <>
              <div className="bp-grid">
                <Gallery product={state.product} />
                <Details product={state.product} />
              </div>
              <More slug={state.product.slug} />
            </>}
          </div>
        </section>
      </Part>
    </Blue2Page>
  </ShopCart>;
}
